import { Business, BusinessServicesData, Member } from '../types'; // Use Zod-inferred Business type, Added Member, Removed AppData
import { BusinessPromotionData } from '@/components/business/onboarding/types/promotionTypes'; // Import BusinessPromotionData
// Portfolio type might be needed if addBusiness updates portfolio.businessRefs
// DeliverySettings type needs to be checked for Zod compatibility
import { ActualBusinessLeadDeliveryData } from '@/schemas/business'; // Using Zod inferred type for lead delivery
import { doc, updateDoc, setDoc, deleteDoc, serverTimestamp, collection } from 'firebase/firestore';
import { db } from '@/utils/firebase';
import { businessesCollection } from '@/utils/firestore/refs'; // Assuming this is CollectionReference<DocumentData>
import { businessSchema } from '@/schemas'; // Import Zod schema for Business

// No longer a factory function, just export the object of utils
export const businessUtils = {
  updateBusiness: async (businessId: string, data: Partial<Business>) => {
      try {
        // Validate partial data
        const validatedData = businessSchema.partial().parse(data);
        
        const businessRef = doc(db, 'businesses', businessId); // Use db directly
        await updateDoc(businessRef, {
          ...validatedData,
          updatedAt: serverTimestamp(),
        });
        console.log(`Successfully updated business ${businessId}`);
      } catch (error) {
        console.error(`Error updating business ${businessId} in Firestore:`, error);
        // Handle or throw error
      }
    },
    
    // portfolioId is needed to link the business.
    // businessId is the client-generated UUID.
    // coreBusinessData should exclude id, portfolioId, createdAt, updatedAt.
    // portfolioId is needed to link the business.
    // businessId is the client-generated UUID.
    // coreBusinessData should exclude id, portfolioId, createdAt, updatedAt.
    addBusiness: async (
      portfolioId: string, 
      businessId: string, // Expect client-generated ID
      coreBusinessData: Omit<Business, 'id' | 'portfolioId' | 'createdAt' | 'updatedAt' | 'members'> & { members?: Array<Omit<Member, 'invitedAt'|'joinedAt'> & { invitedAt: string | null, joinedAt: string | null }> } // Expect ISO string or null for member timestamps
    ) => {
      try {
        // Use the provided businessId for the document reference
        const businessDocRef = doc(db, 'businesses', businessId); 

        const fullBusinessDataForValidation: Omit<Business, 'createdAt' | 'updatedAt'> & { createdAt: null; updatedAt: null } = {
          ...coreBusinessData,
          id: businessId, // Use provided businessId
          portfolioId: portfolioId,
          createdAt: null, 
          updatedAt: null,
          members: coreBusinessData.members, // Assign directly, types should align now
        };
        
        // Validate the data structure (with null or ISO string timestamps for members)
        // Note: Zod schema expects members' invitedAt/joinedAt to be compatible with firestoreTimestampSchema or string or null
        const validatedBusinessData = businessSchema.parse(fullBusinessDataForValidation);

        // Prepare the data for Firestore, now including server timestamps
        const dataForFirestore = {
          ...validatedBusinessData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          // members array from validatedBusinessData already contains ISO strings for invitedAt/joinedAt
          members: validatedBusinessData.members || [], 
        };

        await setDoc(businessDocRef, dataForFirestore); // Use businessDocRef with provided businessId
        console.log(`Successfully added business ${businessId} to portfolio ${portfolioId}`);
        // TODO: Optionally, update the parent portfolio's businessRefs array.
        // This is now handled in useFormSubmit.ts after this function returns.

        return businessId; // Return the provided businessId
      } catch (error) {
        console.error(`Error adding business ${businessId} to Firestore:`, error); // Log with provided businessId
        // Handle or throw error
        throw error; // Re-throw to allow caller to handle
      }
    },
    
    removeBusiness: async (businessId: string) => {
      try {
        const businessRef = doc(db, 'businesses', businessId);
        await deleteDoc(businessRef);
        console.log(`Successfully removed business ${businessId}`);
        // TODO: Optionally, update the parent portfolio's businessRefs array.
        // e.g., await updateDoc(doc(db, 'portfolios', portfolioId), { businessRefs: arrayRemove(businessId) });
      } catch (error) {
        console.error(`Error removing business ${businessId} from Firestore:`, error);
        // Handle or throw error
      }
    },
    
    // For sub-sections, ensure the data passed conforms to the Zod sub-schema if defined,
    // or validate the specific fields.
    updateBusinessServicesData: async (businessId: string, servicesData: BusinessServicesData) => {
      try {
        // BusinessServicesData is type ActualBusinessServicesData from '@/schemas/business'
        // actualServicesDataObjectSchema is the Zod schema for this.
        const { actualServicesDataObjectSchema } = await import('@/schemas/business'); // Dynamically import to avoid circular deps if any
        const validatedServicesData = actualServicesDataObjectSchema.parse(servicesData);

        const businessRef = doc(db, 'businesses', businessId);
        await updateDoc(businessRef, {
          servicesData: validatedServicesData,
          updatedAt: serverTimestamp(),
        });
        console.log(`Successfully updated servicesData for business ${businessId}`);
      } catch (error) {
        console.error(`Error updating servicesData for business ${businessId}:`, error);
        // It's good practice to re-throw or handle more explicitly
        throw error; 
      }
    },
    
    updateBusinessLocationData: async (businessId: string, locationData: any) => {
      // This function had complex validation and event dispatching.
      // Validation should ideally be done via Zod.
      // Event dispatching can be kept if necessary.
      try {
        // locationDataSchema from '@/schemas/business' is actualLocationDataObjectSchema.optional().nullable()
        // The input 'locationData: any' should be parsed against actualLocationDataObjectSchema
        const { actualLocationDataObjectSchema } = await import('@/schemas/business');
        const validatedLocationData = actualLocationDataObjectSchema.parse(locationData);

        const businessRef = doc(db, 'businesses', businessId);
        await updateDoc(businessRef, {
          locationData: validatedLocationData,
          // 'locationData.hasBeenSaved': true, // This should be part of the validatedLocationData if needed
          updatedAt: serverTimestamp(),
        });
        console.log(`Successfully updated locationData for business ${businessId}`);
        
        // Dispatch events if still needed and if hasBeenSaved is true in validatedLocationData
        if (validatedLocationData.hasBeenSaved) {
          const locationsCompletedEvent = new CustomEvent('locationsCompleted', { detail: { businessId, hasBeenSaved: true } });
          document.dispatchEvent(locationsCompletedEvent);
          const sectionCompletedEvent = new CustomEvent('sectionCompleted', { detail: { section: 'locations', businessId, hasBeenSaved: true } });
          document.dispatchEvent(sectionCompletedEvent);
        }
      } catch (error) {
        console.error(`Error updating locationData for business ${businessId}:`, error);
        throw error;
      }
    },
    
    updateBusinessDifferentiatorData: async (businessId: string, differentiatorData: any) => {
      try {
        // differentiatorDataSchema from '@/schemas/business' is actualDifferentiatorDataObjectSchema.optional().nullable()
        // The input 'differentiatorData: any' should be parsed against actualDifferentiatorDataObjectSchema
        const { actualDifferentiatorDataObjectSchema } = await import('@/schemas/business');
        const validatedDifferentiatorData = actualDifferentiatorDataObjectSchema.parse(differentiatorData);

        const businessRef = doc(db, 'businesses', businessId);
        await updateDoc(businessRef, {
          differentiatorData: validatedDifferentiatorData,
          updatedAt: serverTimestamp(),
        });
        console.log(`Successfully updated differentiatorData for business ${businessId}`);
        if (validatedDifferentiatorData.hasBeenSaved) {
            const differentiatorsCompletedEvent = new CustomEvent('differentiatorsCompleted', { detail: { businessId, hasBeenSaved: true } });
            document.dispatchEvent(differentiatorsCompletedEvent);
            const sectionCompletedEvent = new CustomEvent('sectionCompleted', { detail: { section: 'differentiators', businessId, hasBeenSaved: true } });
            document.dispatchEvent(sectionCompletedEvent);
        }
      } catch (error) {
        console.error(`Error updating differentiatorData for business ${businessId}:`, error);
        throw error;
      }
    },
    
    updateBusinessPromotionData: async (businessId: string, promotionData: BusinessPromotionData) => { 
      try {
        // BusinessPromotionData is type ActualBusinessPromotionData from '@/schemas/business' (via promotionTypes.ts)
        // actualPromotionDataObjectSchema is the Zod schema for this.
        const { actualPromotionDataObjectSchema } = await import('@/schemas/business');
        const validatedPromotionData = actualPromotionDataObjectSchema.parse(promotionData);

        const businessRef = doc(db, 'businesses', businessId);
        await updateDoc(businessRef, {
          promotionData: validatedPromotionData,
          updatedAt: serverTimestamp(),
        });
        console.log(`Successfully updated promotionData for business ${businessId}`);
        if (validatedPromotionData.hasBeenSaved) {
            const promotionsCompletedEvent = new CustomEvent('promotionsCompleted', { detail: { businessId, hasBeenSaved: true } });
            document.dispatchEvent(promotionsCompletedEvent);
            const sectionCompletedEvent = new CustomEvent('sectionCompleted', { detail: { section: 'promotions', businessId, hasBeenSaved: true } });
            document.dispatchEvent(sectionCompletedEvent);
        }
      } catch (error) {
        console.error(`Error updating promotionData for business ${businessId}:`, error);
        throw error;
      }
    },
    
    updateBusinessLeadDeliveryData: async (businessId: string, leadDeliveryData: ActualBusinessLeadDeliveryData) => { // Changed DeliverySettings to Zod type
      try {
        // Validate with baseLeadDeliveryDataSchema (from businessSchema.ts) before saving
        // businessSchema.shape.leadDeliveryData is baseLeadDeliveryDataSchema.optional().nullable()
        // So, parse against baseLeadDeliveryDataSchema if leadDeliveryData is not null/undefined.
        const leadDeliveryContentSchema = businessSchema.shape.leadDeliveryData._def.innerType._def.innerType;
        leadDeliveryContentSchema.parse(leadDeliveryData);

        const businessRef = doc(db, 'businesses', businessId);
        await updateDoc(businessRef, {
          leadDeliveryData: leadDeliveryData,
          updatedAt: serverTimestamp(),
        });
        console.log(`Successfully updated leadDeliveryData for business ${businessId}`);
      } catch (error) {
        console.error(`Error updating leadDeliveryData for business ${businessId}:`, error);
        // Re-throw or handle as appropriate
        throw error;
      }
    }
};
